/**
 * El mensaje que el backend quiso dar. Los errores salen como `application/problem+json`: los de
 * validacion traen `errors` —ahi viaja el rechazo del selector de Copeland, que es el que el
 * usuario puede corregir a mano— y el resto traen `detail`.
 */
export const messageOf = (error: any): string | null => {
  const problem = error?.response?.data;

  if (typeof problem !== "object" || problem === null) {
    return null;
  }

  if (typeof problem.detail === "string") {
    return problem.detail;
  }

  const first = Object.values(problem.errors ?? {})[0];

  if (Array.isArray(first) && typeof first[0] === "string") {
    return first[0];
  }

  return typeof problem.title === "string" ? problem.title : null;
};

/**
 * Lo que el servidor explica gana al mapa por codigo: el mapa es una frase fija por status y no
 * sabe **cual** de los cuatrocientos motivos fue. El caso que lo obliga es el centrifugo —Copeland
 * dice que la capacidad en operacion se sale de su envelope, y con eso el usuario la corrige— pero
 * vale para cualquier fallo: sin el, quien lo lee no sabe que tocar.
 */
export const handleApiError = (error: any, customErrors: Record<number, string>): never => {
  const status = error?.response?.status;
  const fromServer = messageOf(error);

  if (fromServer) {
    throw new Error(fromServer);
  }

  if (status && customErrors[status]) {
    throw new Error(customErrors[status]);
  }

  throw new Error(messageForStatus(status));
};

/**
 * Lo que se dice cuando no hay respuesta que leer: un proxy que contesta HTML, un cuerpo vacio,
 * la red caida. Antes era `Unexpected error (502)`, que delante de un usuario no dice ni que paso
 * ni si vale la pena reintentar.
 *
 * En ingles fijo, como el resto de esta capa: los mensajes de servicio no pasan por i18next.
 */
const messageForStatus = (status: number | undefined): string => {
  if (status === undefined) {
    return "Could not reach the server. Check your connection and try again.";
  }

  if (status >= 500) {
    return "The server could not complete the operation. Try again in a few moments.";
  }

  switch (status) {
    case 400:
      return "The data sent is not valid.";
    case 401:
      return "Your session is not valid. Sign in again.";
    case 403:
      return "You do not have permission to do this.";
    case 404:
      return "What you asked for was not found.";
    case 409:
      return "Another change got there first. Reload and try again.";
    default:
      return `The operation failed (${status}).`;
  }
};

/**
 * El texto que se enseña cuando algo falla. Sirve tanto si llega el error de axios crudo como si
 * ya paso por `handleApiError`, que deja el mensaje del servidor en un `Error` pelado.
 */
export const errorMessage = (error: any, fallback: string): string =>
  messageOf(error) ?? (typeof error?.message === "string" ? error.message : fallback);
